import { createContext, useContext, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentsApi, SubscriptionStatusResponse } from '../api/paymentsApi';
import { useUser } from './UserContext';

interface PremiumContextType {
  isPremium: boolean;
  subscriptionStatus: string;
  currentPlan?: string;
  hasFeatureAccess: (featureName: string) => boolean;
  refreshSubscription: () => Promise<void>;
  subscriptionData?: SubscriptionStatusResponse;
  isLoading: boolean;
}

const PremiumContext = createContext<PremiumContextType>({
  isPremium: false,
  subscriptionStatus: 'Free',
  hasFeatureAccess: () => false,
  refreshSubscription: async () => {},
  isLoading: false
});

interface PremiumProviderProps {
  children: ReactNode;
}

export const PremiumProvider = ({ children }: PremiumProviderProps) => {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();

  const { data: subscription, isLoading, error } = useQuery({
    queryKey: ['subscription', currentUser?.email],
    queryFn: () => paymentsApi.getSubscriptionStatus(currentUser?.email || ''),
    enabled: !!currentUser?.email,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    retryOnMount: false
  });

  const isPremium = subscription?.tier === 'Premium' && subscription?.status === 'Active';

  const hasFeatureAccess = (featureName: string): boolean => {
    // If there's an error or no subscription data, assume free user (no premium features)
    if (error || !subscription?.features) return false;
    return subscription.features.includes(featureName);
  };

  const refreshSubscription = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['subscription'] });
  };

  const value: PremiumContextType = {
    isPremium,
    subscriptionStatus: subscription?.status || 'Free',
    currentPlan: subscription?.currentSubscription?.planName,
    hasFeatureAccess,
    refreshSubscription,
    subscriptionData: subscription,
    isLoading
  };

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};

export const usePremiumFeatures = (): PremiumContextType => {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error('usePremiumFeatures must be used within PremiumProvider');
  }
  return context;
};